// src/hooks/useLiveRates.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchGoldRates } from '../services/goldApi';
import { fetchSilverRates } from '../services/silverApi';

// Format product data (works for both gold and silver)
const formatProductData = (product, type = 'gold') => {
  let purity = '24K';
  let metal = type === 'gold' ? 'Gold' : 'Silver';
  let displayName = product.name;
  let unit = product.unit || (type === 'gold' ? 'gram' : 'kg');
  
  if (type === 'gold') {
    if (product.name.includes('995')) purity = '995';
    if (product.name.includes('999')) purity = '999';
    if (product.name.includes('SPOT')) {
      purity = 'Spot';
      displayName = 'Gold Spot';
    }
    if (product.name.includes('COSTING')) displayName = 'Gold Costing';
    if (product.name.includes('INR')) {
      metal = 'USD/INR';
      displayName = 'USD/INR';
    }
  } else {
    // Silver formatting
    if (product.name.includes('999')) purity = '999';
    if (product.name.includes('SPOT')) {
      purity = 'Spot';
      displayName = 'Silver Spot';
    }
    if (product.name.includes('BAR')) displayName = 'Silver Bar';
    if (product.name.includes('GRANULES')) displayName = 'Silver Granules';
    if (product.name.includes('COSTING')) displayName = 'Silver Costing';
    if (product.name.includes('(incl. GST)')) displayName = displayName + ' (incl. GST)';
  }

  // Determine price unit display
  if (type === 'silver' && unit === 'kg') {
    // Keep as kg for silver
  }

  return {
    id: product.id,
    metal: metal,
    purity: purity,
    name: displayName,
    price: product.price,
    bid: product.price * 0.995,
    ask: product.ask || product.price * 1.005,
    high: product.high || product.price * 1.01,
    low: product.low || product.price * 0.99,
    change: 0,
    unit: unit,
    source: product.source || (type === 'gold' ? 'KakaGold' : 'KakaGold Silver'),
    raw: product,
    type: type
  };
};

export const useLiveRates = () => {
  const [rates, setRates] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const intervalRef = useRef(null);
  const priceHistoryRef = useRef({});
  
  // Generate fallback data
  const generateFallbackData = useCallback(() => {
    console.log('🔄 Generating fallback data...');
    const fallbackProducts = [
      // Gold fallback
      { id: 'GOLD_FB_1', name: 'GOLD SPOT', price: 4252.80 + (Math.random() - 0.5) * 10, unit: 'oz' },
      { id: 'GOLD_FB_2', name: 'GOLD 995', price: 146892 + (Math.random() - 0.5) * 100, unit: 'gram' },
      { id: 'GOLD_FB_3', name: 'GOLD 999', price: 147492 + (Math.random() - 0.5) * 100, unit: 'gram' },
      // Silver fallback
      { id: 'SILVER_FB_1', name: 'SILVER SPOT', price: 62.33 + (Math.random() - 0.5) * 0.5, unit: 'oz' },
      { id: 'SILVER_FB_2', name: 'SILVER 999 BAR', price: 224883 + (Math.random() - 0.5) * 500, unit: 'kg' },
      { id: 'SILVER_FB_3', name: 'SILVER GRANULES', price: 224883 + (Math.random() - 0.5) * 500, unit: 'kg' }
    ];
    
    const formattedRates = fallbackProducts.map(product => {
      const type = product.id.includes('GOLD') ? 'gold' : 'silver';
      console.log(product)
      const formatted = formatProductData(product, type);
      formatted.source = 'Fallback Data';
      return formatted;
    });
    
    setRates(formattedRates);
    setLastUpdate(new Date());
  }, []);

  const fetchAllRates = useCallback(async () => {
    try {
      console.log('🔄 Fetching all rates...');
      
      // Fetch both gold and silver in parallel
      const [goldProducts, silverProducts] = await Promise.all([
        fetchGoldRates().catch(err => {
          console.warn('⚠️ Gold API error:', err.message);
          return [];
        }),
        fetchSilverRates().catch(err => {
          console.warn('⚠️ Silver API error:', err.message);
          return [];
        })
      ]);

      // Format products
      const formattedGold = goldProducts.map(p => formatProductData(p, 'gold'));
      const formattedSilver = silverProducts.map(p => formatProductData(p, 'silver'));
      
      const allFormatted = [...formattedGold, ...formattedSilver];

      // If no products from either API, use fallback
      if (allFormatted.length === 0) {
        console.warn('⚠️ No products from any API, using fallback');
        generateFallbackData();
        return;
      }

      // Calculate changes and update history
      const updatedRates = allFormatted.map(product => {
        const id = product.id;
        const prevData = priceHistoryRef.current[id];
        const prevPrice = prevData?.price || product.price;
        const change = prevPrice ? ((product.price - prevPrice) / prevPrice) * 100 : 0;
        product.change = Math.round(change * 100) / 100;
        
        priceHistoryRef.current[id] = {
          price: product.price,
          change: product.change,
          timestamp: new Date()
        };
        
        return product;
      });

      setRates(updatedRates);
      setLastUpdate(new Date());
      setIsConnected(true);
      setError(null);
      setUsingFallback(false);
      
      console.log(`✅ Rates updated: Gold: ${formattedGold.length}, Silver: ${formattedSilver.length}, Total: ${updatedRates.length}`);
      
    } catch (error) {
      console.error('❌ Error fetching rates:', error);
      setError('Failed to fetch rates. Retrying...');
      setIsConnected(false);
      setUsingFallback(true);
      generateFallbackData();
    }
  }, [generateFallbackData]);

  // Start polling every 2 seconds
  const startPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    fetchAllRates();
    intervalRef.current = setInterval(fetchAllRates, 2000);
  }, [fetchAllRates]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsConnected(false);
  }, []);

  useEffect(() => {
    startPolling();
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  return {
    rates,
    isConnected,
    error,
    lastUpdate,
    usingFallback,
    reconnect: startPolling,
    disconnect: stopPolling,
  };
};